/**
 * API Client V2 - Cookie-based authentication (XSS-safe)
 *
 * Security improvements:
 * - Tokens stored in httpOnly cookies (not accessible to JavaScript)
 * - Token refresh queue prevents race conditions
 * - Credentials: 'include' ensures cookies are sent with requests
 * - No localStorage = No XSS token theft risk
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { tokenRefreshQueue } from '@/utils/tokenRefreshQueue';
import { AUTH_ENDPOINTS } from '@/constants/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

export const apiClientV2: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 180000, // 3 minutes (safety net para uploads grandes e polling)
  withCredentials: true, // CRITICAL: Send cookies with requests
  maxContentLength: 100 * 1024 * 1024, // 100MB
  maxBodyLength: 100 * 1024 * 1024, // 100MB
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Kaive-Audience': 'client',
  },
});

/**
 * Request interceptor
 * No need to manually add Authorization header - cookies are sent automatically
 */
apiClientV2.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Cookies are sent automatically via withCredentials: true

    // If data is FormData, remove Content-Type to let axios set it with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - handles token refresh on 401
 */
apiClientV2.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only attempt refresh for 401 errors
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // Don't retry refresh endpoint itself
    if (originalRequest.url?.includes(AUTH_ENDPOINTS.REFRESH)) {
      return Promise.reject(error);
    }

    // Avoid infinite retry loops
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      // Use the queue to refresh token (handles concurrent requests)
      const success = await tokenRefreshQueue.refresh();

      if (!success) {
        return Promise.reject(error);
      }

      // Retry the original request - new cookie will be sent automatically
      return apiClientV2(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

export default apiClientV2;
